import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import ControlledTags from '@/components/form/controlled-tags';

export type CommunicationPurposeOption = {
  value: string;
  i18nKey: string;
  tooltipi18nKey: string;
  subcategories: Array<{
    value: string;
    i18nKey: string;
    icons: string[];
  }>;
};

type CommunicationPurposeProps = {
  purposes?: CommunicationPurposeOption[];
  titleKey?: string;
};

function CommunicationPurpose({
  purposes,
  titleKey = 'label.communicationPurpose',
}: CommunicationPurposeProps) {
  const { t } = useTranslation();
  const {
    watch,
    formState: { errors },
    formState,
    setValue,
    getFieldState,
  } = useFormContext();

  const selectedPurpose = watch('communicationPurpose');

  const communicationPurposes = (purposes || []).map(
    ({ tooltipi18nKey, value, i18nKey, subcategories }) => ({
      value,
      i18nKey,
      ...(tooltipi18nKey && {
        tooltipText: (
          <Trans
            i18nKey={tooltipi18nKey}
            components={{
              br: <br />,
              ul: <ul className="list-disc pl-5" />,
              li: <li />,
              list: (
                <ul className="list-disc pl-5">
                  {subcategories?.map((subcategory) => (
                    <li key={subcategory.value}>{t(subcategory.i18nKey)}</li>
                  ))}
                </ul>
              ),
            }}
            values={{
              list: '<list />',
            }}
          />
        ),
      }),
    })
  );

  const subcategories = purposes
    ?.find((purpose) => purpose.value === selectedPurpose)
    ?.subcategories.map((subcategory) => {
      return {
        value: subcategory.value,
        i18nKey: subcategory.i18nKey,
        iconName: subcategory.icons[0],
      };
    });

  const { isDirty } = getFieldState('communicationPurpose', formState);
  useEffect(() => {
    if (isDirty) setValue('subcategory', subcategories?.[0]?.value);
  }, [selectedPurpose]);

  return (
    <>
      <p className="leading-normal font-bold mb-4">{t(titleKey)}</p>
      <ControlledTags
        name={'communicationPurpose'}
        errors={errors}
        options={communicationPurposes}
        multiple={false}
      />
      {subcategories && (
        <div className="mt-4 mb-4">
          <p className="leading-normal font-bold mb-4">
            {t('label.subcategories')}
          </p>
          <div className="flex gap-2 flex-wrap">
            <ControlledTags
              name={'subcategory'}
              errors={errors}
              options={subcategories}
              multiple={false}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default CommunicationPurpose;
